import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { logger } from "@/lib/logger";

interface Props {
  children: ReactNode;
  /** When this changes (e.g. the user navigates) the boundary auto-recovers. */
  resetKey: string;
}

interface State {
  hasError: boolean;
}

/**
 * Backstop around the routed app shell. Without it, any render crash below the
 * data router — a missing authenticated context after an Auth0 state flip, a bad
 * render in a page — bubbles to React Router's generic "Unexpected Application
 * Error" screen and never reaches Application Insights, so prod incidents are
 * undiagnosable. Wraps AuthenticatedProviders so it still renders when those
 * providers are the thing that went missing.
 */
class AppShellErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error("App shell render crashed", {
      operation: "renderAppShell",
      error,
      componentStack: errorInfo.componentStack ?? undefined,
      pathname: this.props.resetKey,
    });
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  private handleRetry = () => {
    this.setState({ hasError: false });
  };

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex h-screen w-full flex-col items-center justify-center gap-3 px-4 text-center text-muted-foreground">
          <AlertTriangle className="size-10 opacity-40" />
          <p className="max-w-md text-sm">
            Der opstod en uventet fejl. Prøv igen, eller genindlæs siden.
          </p>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={this.handleRetry}>
              Prøv igen
            </Button>
            <Button variant="secondary" size="sm" onClick={this.handleReload}>
              Genindlæs siden
            </Button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default AppShellErrorBoundary;
